import { useSelector, useDispatch } from "react-redux";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import InfoIcon from "@mui/icons-material/Info";
import { Stack, Typography, Tooltip, IconButton, Grid, Button } from "@financeable-com-au/financeable-ui";
import { getIsCommercialOrBusinessApplication, ERRORS_MESSAGE, DOCUMENT_TEMPLATE_TYPE, APPLICATION_DOWNLOAD_TYPE } from "@/constants";
import {
  applicationSelector,
  userDetailsSelector,
  getApplicationDownloadUrl,
  updateLoanDetails,
  getApplicationDocumentPreview,
  saveValue,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import {
  type ApplicationFormSlice,
  type ApplicationState,
  type UserDetailsState,
} from "@/store/slices/types/applicationFormSlice";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
import { formatFeesForSaveQuote } from "@/utils/lenderUtils";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { useTemplateServices } from "@/hooks";
import { IDispatchResponse } from "@/utils/interface";
import { UserPermissionsDocument, UserSlice } from "@/store/slices/types/userSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userPermissionsSelector } from "@/store/slices/userSlice";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import {
  DownloadApplicationButton,
  type ApplicationDownloadType,
} from "@/components/CommonComponents/DownloadApplicationButton";

const QuoteDisplay = () => {
  const { canUseDisclosureServices } = useTemplateServices();
  const { accessComponents } = useAccessAppComponent();
  const { defaultApplicationDownloadType } = accessComponents.application;
  const {
    lenderCardCustom: { rate, loanAmount, financeAmount, brokerage, fees },
    quoteCalculatorFormik,
  } = useLenderCardContext();
  const {
    applicationType,
    canEditApplication,
    customers,
    _id: applicationId,
    loanDetails: { _id: loanDetailsId },
  } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);

  const permissions = useSelector<UserSlice, UserPermissionsDocument>(userPermissionsSelector);
  const { downloadApplicationLoader, disclosureLoader } = useSelector<ApplicationFormSlice, UserDetailsState>(
    userDetailsSelector,
  );
  const isDisableEdit = !permissions?.application?.edit || !canEditApplication;
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(applicationType);

  const dispatch = useDispatch();
  const handle = {
    onApplicationDownload: (downloadType: ApplicationDownloadType) => {
      dispatch(getApplicationDownloadUrl({ applicationId, downloadType }));
    },
    getFeeValue: (key: string) =>
      Number(quoteCalculatorFormik?.values?.fees?.find((feeObj) => feeObj?.key === key)?.value) || 0,
    submitDisclosure: async () => {
      dispatch(saveValue({ disclosureLoader: true }));

      const { lender, repayments, repaymentFrequency, referrer } = quoteCalculatorFormik?.values ?? {};
      const commonFields = {
        rate: rate?.default?.value,
        lender: lender,
        productType: "",
        loanAmount: loanAmount?.value || 0,
        repayments: Number(repayments) || 0,
        fees: formatFeesForSaveQuote(fees?.application ?? []),
        originationFee: handle.getFeeValue("originationFee"),
      };

      const customQuoteFields = {
        ...commonFields,
        applicationId,
        effectiveRate: rate?.effective?.value || 0,
        repaymentFrequency: repaymentFrequency, // do not save it to loan details
        referrer: referrer,
      };

      if (loanDetailsId) {
        await dispatch(
          updateLoanDetails({
            applicationId,
            ...commonFields,
            _id: loanDetailsId,
            brokerage: brokerage?.percentage?.value,
            brokerageAmount: brokerage?.amount?.value,
          }),
        ).unwrap();
      }

      const payload = {
        customerId: customers?.[0]._id,
        applicationId,
        templateType: DOCUMENT_TEMPLATE_TYPE.CUSTOMER_DISCLOSURE,
        disclosureInfo: {
          ...customQuoteFields,
        },
      };

      dispatch(getApplicationDocumentPreview(payload)).then((res: IDispatchResponse) => {
        if (res.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          // close disclosure modal and open preview document modal
          dispatch(
            saveValue({
              disclosureLoader: false,
              showPreviewDocument: {
                isOpen: true,
                customerId: payload?.customerId,
                templateType: payload?.templateType,
                disclosureInfo: payload?.disclosureInfo,
              },
            }),
          );
        }
      });
    },
  };
  const isCustomerEmail = customers?.[0]?.emails?.length > 0;
  const isSendDisclosureDisabled =
    !quoteCalculatorFormik?.values.lender || !isCustomerEmail || !canUseDisclosureServices;

  let sendDisclosureInfoText = "";

  if (!quoteCalculatorFormik?.values.lender) {
    sendDisclosureInfoText = "Please select a lender";
  } else if (!isCustomerEmail) {
    sendDisclosureInfoText = "Please enter a valid email address";
  } else if (!canUseDisclosureServices) {
    sendDisclosureInfoText = "Please add disclosure document template word file";
  }

  return (
    <Stack flexDirection="column" rowGap={4} flexGrow={1}>
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
        <Typography level="body-lg" bold>
          Manual Quote
        </Typography>
      </Stack>
      <Grid container sm={12} spacing={1}>
        <Grid sm={6}>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
            <Typography level="body-xs" textColor="neutral.500">
              Comparison Rate
            </Typography>
            <Typography level="body-xs" bold>
              {rate.comparison.formattedValue}
            </Typography>
          </Stack>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
            <Stack flexDirection="row" alignItems="center" justifyContent="flex-start">
              <Typography level="body-xs" textColor="neutral.500">
                Loan Amount
              </Typography>
              <Tooltip
                title={
                  <Stack padding={2} flexDirection="column" rowGap={2}>
                    <Typography>
                      Includes total financed fees of <Typography bold>{fees.totalFinanced.formattedValue}</Typography>
                    </Typography>
                  </Stack>
                }
              >
                <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                  <InfoIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography level="body-xs" bold>
              {loanAmount.formattedValue}
            </Typography>
          </Stack>
          {isCommercialOrBusinessApplication && (
            <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
              <Stack flexDirection="row" alignItems="center" justifyContent="flex-start">
                <Typography level="body-xs" textColor="neutral.500">
                  Finance Amount
                </Typography>
                <Tooltip
                  title={
                    <Stack padding={2} flexDirection="column" rowGap={2}>
                      <Typography>
                        {/* Includes brokerage amount of <Typography bold>{brokerage.amount.formattedValue}</Typography> */}
                        Includes brokerage amount of <Typography bold>{brokerage.amount.formattedValue}</Typography>
                      </Typography>
                    </Stack>
                  }
                >
                  <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                    <InfoIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography level="body-xs" bold>
                {financeAmount.formattedValue}
              </Typography>
            </Stack>
          )}
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
            <Typography level="body-xs" textColor="neutral.500">
              Upfront / Financed Fees
            </Typography>
            <Typography level="body-xs" bold>
              {fees.totalUpfront.formattedValue} | {fees.totalFinanced.formattedValue}
            </Typography>
          </Stack>
        </Grid>
        <Grid sm={6} display="flex" justifyContent="flex-end">
          <Stack flexDirection="row" flexWrap="wrap" columnGap={3} rowGap={1}>
            <Stack flexDirection="row" alignItems="flex-end" gap={1}>
              <Button
                variant="secondary"
                intent="neutral"
                size="sm"
                disabled={isDisableEdit || isSendDisclosureDisabled || disclosureLoader}
                endDecorator={<SendOutlinedIcon sx={{ fontSize: "1rem" }} />}
                onClick={handle.submitDisclosure}
              >
                {disclosureLoader ? "Getting document..." : "Preview Disclosure"}
              </Button>
              {sendDisclosureInfoText && (
                <Tooltip
                  title={
                    <Stack padding={2} flexDirection="column" rowGap={2}>
                      <Typography>{sendDisclosureInfoText}</Typography>
                    </Stack>
                  }
                >
                  <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                    <InfoIcon sx={{ fontSize: "1rem", mb: 1 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack alignItems="flex-end" justifyContent="flex-end">
              <DownloadApplicationButton
                defaultDownloadType={defaultApplicationDownloadType || APPLICATION_DOWNLOAD_TYPE.PDF}
                isLoading={downloadApplicationLoader}
                disabled={isDisableEdit}
                onDownload={handle.onApplicationDownload}
              />
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export { QuoteDisplay };
